import styled from 'styled-components';
import {
  TextField,
  Tabs,
  Tab,
  Chip,
  Typography,
  Alert,
  ToggleButtonGroup,
  FormHelperText,
  Button as MuiButton,
} from '@mui/material';
import { IonAvatar } from '@ionic/react';

import colors from '../../../../../styles/colors';
import Button from '../../../../../components/Button';
import { AVATAR_PIXEL_SIZE } from './constants';

const ModalBody = styled.div`
  position: relative;
  overflow: auto;
`;

const StyledTabs = styled(Tabs)`
  width: 100%;
  padding: 0 20px;
  border-bottom: 1px solid ${colors.shades.secondary4};
  background: ${colors.base.alpha};
  box-sizing: border-box;

  .MuiTabs-indicator {
    background-color: ${colors.shades.secondary3};
  }
`;

const StyledTab = styled(Tab)`
  display: flex;
  flex-direction: row;
  margin-right: 20px;
  padding: 0;
  text-transform: none;
  font-weight: 400;
  color: ${colors.shades.secondary3};
  &.Mui-selected {
    color: ${colors.shades.secondary3};
  }
`;

const TabContainer = styled.div`
  padding: 24px 32px;
`;

/* ---- Sections ---- */

const SectionContainer = styled.div`
  margin-bottom: 28px;
`;

const SectionLabel = styled(Typography)`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${colors.shades.gamma2};
  margin: 10px 0 5px 0;
`;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  margin-bottom: 12px;
  width: fit-content;

  .MuiToggleButton-root {
    text-transform: none;
    font-size: 13px;
    padding: 5px 18px;
    color: ${colors.shades.secondary8};
    border-color: ${colors.shades.secondary4};

    &.Mui-selected {
      background-color: ${colors.shades.secondary3};
      color: ${colors.base.alpha};
      border-color: ${colors.shades.secondary3};

      &:hover {
        background-color: ${colors.shades.secondary8};
      }
    }
  }
`;

const SearchBar = styled(TextField)`
  width: 100%;
  margin-bottom: 8px;

  .MuiInputBase-root {
    background-color: ${colors.base.alpha};
    font-size: 14px;
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${colors.shades.secondary4};
  }
`;

const SelectAllButton = styled(MuiButton)`
  padding: 0;
  font-size: 12px;
  color: ${colors.base.secondary};
  text-decoration: underline;
  margin-bottom: 8px;
  min-width: unset;
  text-transform: none;

  &:hover {
    background: none;
    color: ${colors.shades.secondary1};
    text-decoration: underline;
  }

  &.Mui-disabled {
    color: ${colors.shades.gamma2};
    text-decoration: none;
  }
`;

const StyledListContainer = styled.div`
  max-height: 230px;
  overflow-y: auto;
  border: 1px solid ${colors.shades.secondary4};
  border-radius: 6px;
  background: ${colors.base.alpha};
`;

const RecipientRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid ${colors.shades.secondary4};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${colors.shades.secondary5};
  }
`;

const StyledAvatar = styled(IonAvatar)`
  width: ${AVATAR_PIXEL_SIZE}px;
  height: ${AVATAR_PIXEL_SIZE}px;
  margin-right: 10px;
  flex-shrink: 0;
`;

const RecipientInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const RecipientName = styled(Typography)`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.shades.secondary3};
`;

const RecipientSubText = styled(Typography)`
  font-size: 11px;
  color: ${colors.shades.gamma2};
  margin-top: 1px;
`;

/* ---- Message section ---- */

const PersonalizeChipsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const PersonalizeChip = styled(Chip)`
  font-size: 12px;
  cursor: pointer;
  border-color: ${colors.shades.secondary4};
  color: ${colors.base.secondary};

  &:hover {
    background-color: ${colors.shades.secondary5};
    border-color: ${colors.base.secondary};
  }
`;

const StyledTextField = styled(TextField)`
  width: 100%;
  background: ${colors.base.alpha};

  .MuiOutlinedInput-notchedOutline {
    border-color: transparent;
    border: 1px solid ${colors.shades.secondary4};
  }
`;

const MessageToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
`;

const AttachmentsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  & > * {
    width: 100%;
    margin: 4px 0;
    box-sizing: border-box;
  }
`;

/* ---- Delivery section ---- */

const DateSelectorContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin: 10px 0;
  flex-wrap: wrap;
`;

const DateSelectorField = styled(TextField)`
  flex: 1;
  min-width: 150px;

  .MuiInputBase-root {
    background-color: ${colors.base.alpha};
    font-size: 14px;
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${colors.shades.secondary4};
  }
`;

/* ---- Options section ---- */

const OptionsRow = styled.div`
  display: flex;
  align-items: center;

  .MuiFormControlLabel-root {
    margin-left: 0;
  }
`;

const HelperText = styled(FormHelperText)`
  font-size: 12px;
  color: ${({ $isWarning }) => ($isWarning ? colors.base.warning : colors.shades.gamma2)};
  margin: 0 0 4px 4px;
  line-height: 1.4;

`;

const SendBroadcastButton = styled(Button)`
  border-radius: 5px;
  background: ${({ disabled }) => (disabled ? colors.shades.gamma1 : colors.shades.beta2)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  color: ${({ disabled }) => (disabled ? colors.shades.gamma2 : colors.base.alpha)};
  text-transform: none;
  height: fit-content;
  font-size: 14px;
  line-height: 16px;
  width: 100%;
  margin-top: 0;
`;

const StyledAlert = styled(Alert)`
  margin-bottom: 12px;
`;

const ConflictWarning = styled.span`
  font-size: 11px;
  color: ${colors.base.danger};
  margin-top: 3px;
  display: block;
`;

const StyledLabel = styled(Typography)`
  display: flex;
  align-items: center;
  font-weight: bold;
  font-size: 10px;
  margin-bottom: 4px;
  color: ${colors.base.primary};
`;

export {
  ModalBody,
  StyledTabs,
  StyledTab,
  TabContainer,
  SectionContainer,
  SectionLabel,
  StyledToggleButtonGroup,
  SearchBar,
  SelectAllButton,
  StyledListContainer,
  RecipientRow,
  StyledAvatar,
  RecipientInfo,
  RecipientName,
  RecipientSubText,
  PersonalizeChipsContainer,
  PersonalizeChip,
  StyledTextField,
  MessageToolbar,
  AttachmentsWrapper,
  DateSelectorContainer,
  DateSelectorField,
  OptionsRow,
  HelperText,
  SendBroadcastButton,
  StyledAlert,
  StyledLabel,
  ConflictWarning,
};
